import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import ScrollToTop from '../components/ScrollToTop';
import DocsNavigator from '../components/DocsNavigator';
import VideoPlayer from '../components/VideoPlayer';
import TeamMember from '../components/TeamMember';
import GitHubButton from '../components/GitHubButton';

// Import SVG as component
import { ReactComponent as MCPZeroSVG } from '../assets/svg/mcp-zero.svg';

const PageContainer = styled.div`
  padding-top: 2rem;
`;

const HeroSection = styled.section`
  background: linear-gradient(to right, #0d47a1, #1976d2);
  color: white;
  padding: 4rem 2rem;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  margin-bottom: 1.5rem;
`;

const Subtitle = styled(motion.h2)`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  font-weight: 400;
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 3rem auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: #0066cc;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #0066cc;
`;

const CardContent = styled.p`
  line-height: 1.6;
  color: #555;
`;

const DiagramContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 3rem auto;
`;

const DiagramTitle = styled.h3`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.8rem;
  color: #333;
`;

const AnimationContainer = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 3rem auto;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const MCPZero = () => {
  const animationRef = useRef(null);
  const [activeDocSection, setActiveDocSection] = useState('overview');
  
  // Mock documentation sections for MCP Zero
  const docSections = {
    overview: { title: 'Overview', content: '# MCP Zero\n\nMCP Zero is a production-grade AI agent infrastructure with resource constraints, ethical governance, and cryptographic integrity.' },
    architecture: { title: 'Architecture', content: '# Architecture\n\nThe architecture of MCP Zero consists of multiple layers that work together to provide a robust and secure environment for AI agents.' },
    installation: { title: 'Installation', content: '# Installation\n\n```bash\nnpm install mcp-zero\n```\n\nOr use Docker:\n\n```bash\ndocker pull mcp-zero/core:latest\n```' },
    tutorials: { title: 'Tutorials', content: '# Tutorials\n\n## Creating your first agent\n\n1. Initialize the MCP environment\n2. Define agent constraints\n3. Implement verification rules' }
  };

  useEffect(() => {
    // Load the animation in the iframe
    if (animationRef.current) {
      animationRef.current.setAttribute('src', '/assets/images/mcp-zero-animation.html');
    }
  }, []);

  return (
    <PageContainer>
      <ScrollToTop background="#0066cc" />
      <HeroSection>
        <HeroContent>
          <Title
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            MCP Zero
          </Title>
          <Subtitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Production-grade AI agent infrastructure with resource constraints, ethical governance, and cryptographic integrity
          </Subtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <SectionTitle>Overview</SectionTitle>
        <p>
          MCP Zero is an advanced platform for building offline-first resilient AI-powered applications. 
          The core design principle is that all components start in offline mode and maintain functionality 
          regardless of connectivity status, making MCP Zero ideal for applications that need guaranteed 
          operation in all environments.
        </p>
        
        <DiagramContainer>
          <DiagramTitle>MCP Zero Architecture</DiagramTitle>
          <MCPZeroSVG width="100%" height="auto" />
        </DiagramContainer>
        
        <AnimationContainer>
          <VideoPlayer 
            src="https://youtu.be/2LfCaVryKE0" 
            title="MCP Zero"
          />
        </AnimationContainer>
        
        <GitHubButton url="https://github.com/GlobalSushrut/mcp-zero">
          View MCP Zero on GitHub
        </GitHubButton>
        
        <SectionTitle>Core Principles</SectionTitle>
        <Grid>
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CardTitle>Offline-First Resilience</CardTitle>
            <CardContent>
              Every component begins in offline mode, tries to connect to remote services exactly once,
              permanently switches to offline mode on connection failure, maintains full functionality using local resources,
              and enhances features when remote services are available.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <CardTitle>Ethical Governance</CardTitle>
            <CardContent>
              MCP Zero incorporates a comprehensive ethical governance framework that ensures
              AI agents operate within established guidelines, with transparency and accountability
              built into every level of the system.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <CardTitle>Cryptographic Integrity</CardTitle>
            <CardContent>
              All operations and data exchanges are secured with cryptographic proofs,
              ensuring the integrity of the system even in contested environments.
              This creates an auditable trail of actions and decisions.
            </CardContent>
          </Card>
        </Grid>
        
        <SectionTitle>Applications</SectionTitle>
        <p>
          While the editor is the most straightforward implementation, MCP Zero enables building:
        </p>
        <ul>
          <li>Autonomous Agent Systems that function independently offline</li>
          <li>Knowledge Management Applications with persistent local state</li>
          <li>Edge Computing Solutions for network-constrained environments</li>
          <li>Field Service Applications for remote work scenarios</li>
          <li>Healthcare and Critical Systems requiring guaranteed operation</li>
          <li>Research and Educational Tools for varied connectivity environments</li>
        </ul>
        
        <SectionTitle>IoT and Robotics Applications</SectionTitle>
        <p>
          MCP Zero has been successfully implemented across various domains:
        </p>
        <Grid>
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CardTitle>Agricultural & Environmental IoT</CardTitle>
            <CardContent>
              Includes precision irrigation, crop health monitoring, livestock monitoring, 
              environmental stations, soil analysis, and more applications that function
              reliably in rural environments with limited connectivity.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <CardTitle>Robotics & Autonomous Systems</CardTitle>
            <CardContent>
              Powers delivery robots, industrial robotic arms, warehouse robots,
              mobile equipment, collaborative robots, and other systems that require
              continuous operation regardless of network availability.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <CardTitle>Smart City & Infrastructure</CardTitle>
            <CardContent>
              Enables traffic management, smart parking, smart grid, public safety,
              waste management, and other critical urban infrastructure that must remain
              functional during network outages or emergencies.
            </CardContent>
          </Card>
        </Grid>
      </ContentSection>
      
      <ContentSection>
        <SectionTitle>Founder</SectionTitle>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <TeamMember
            name="Umesh Adhikari"
            role="Founder & Lead Architect"
            image="/assets/images/team/umesh-adhikari.jpg"
            bio="Umesh Adhikari is a visionary technologist, philosopher, and self-taught innovator, forging new frontiers where mathematics, artificial intelligence, and decentralized systems converge. With roots grounded in determination and a spirit shaped by resilience, Umesh embodies the rare balance of humility and brilliance. From the bustling kitchens of his early days to the deep silence of code and theory, his journey is a testament to transformation — not just personal, but generational. Fueled by curiosity and an unshakable belief in building what the world has yet to imagine, Umesh is the mind behind frameworks like the Advanced Unified Intelligence Framework (AUIF) and Transition Numbers Theory — pioneering languages of logic that challenge the boundaries of science, computation, and consciousness itself."
            tags={['AI Architecture', 'Decentralized Systems', 'MCP Protocol Design']}
            socials={[
              { platform: 'github', url: 'https://github.com/GlobalSushrut' },
              { platform: 'linkedin', url: '#' },
              { platform: 'email', url: 'mailto:umeshlamton@gmail.com' }
            ]}
            accentColor="#0066cc"
          />
        </div>
      </ContentSection>
      
      <ContentSection>
        <SectionTitle>Documentation</SectionTitle>
        <DocsNavigator 
          sections={[
            { 
              title: 'Documentation', 
              pages: [
                { id: 'overview', title: 'Overview', content: docSections.overview.content },
                { id: 'architecture', title: 'Architecture', content: docSections.architecture.content },
                { id: 'installation', title: 'Installation', content: docSections.installation.content },
                { id: 'tutorials', title: 'Tutorials', content: docSections.tutorials.content }
              ]
            }
          ]}
          initialPage={{ id: 'overview', title: 'Overview', content: docSections.overview.content }}
          accentColor="#0066cc"
        />
      </ContentSection>
    </PageContainer>
  );
};

export default MCPZero;
